import type { RefObject } from 'react';

export const getCleanCanvasContext = (
  canvas: HTMLCanvasElement | null,
): CanvasRenderingContext2D => {
  if (!canvas) {
    throw Error('HTMLCanvasElement element missing');
  }

  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw Error('Could not get CanvasRenderingContext2D');
  }

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  return ctx;
};

const drawHeat = ({
  context,
  heatmapImage,
  width,
  height,
  includeOverlay,
}: {
  context: CanvasRenderingContext2D;
  heatmapImage: HTMLImageElement;
  width: number;
  height: number;
  includeOverlay: boolean;
}) => {
  if (includeOverlay === true) {
    context.fillStyle = 'rgba(0, 0, 0, 0.25)';
    context.fillRect(0, 0, width ?? 0, height ?? 0);
  }

  context.globalAlpha = 0.75;
  context.drawImage(heatmapImage, 0, 0);
};

/**
 * Use this to render a screenshot and a heatmap image on a canvas element.
 */
export const screenshotCanvasRenderer = (
  canvas: HTMLCanvasElement | null,
  screenshotImage: HTMLImageElement,
): void => {
  const context = getCleanCanvasContext(canvas);

  context.drawImage(screenshotImage, 0, 0);
};

export const heatCanvasRenderer = (
  canvas: HTMLCanvasElement | null,
  heatmapImage: HTMLImageElement,
  includeOverlay: boolean,
): void => {
  const context = getCleanCanvasContext(canvas);

  drawHeat({
    context,
    heatmapImage,
    includeOverlay,
    width: canvas?.width ?? 0,
    height: canvas?.height ?? 0,
  });
};

export const layersCanvasRenderer = (
  visibleLayers: RefObject<HTMLCanvasElement>[],
): HTMLCanvasElement | null => {
  if (visibleLayers.length === 0) {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = visibleLayers[0].current?.width ?? 0;
  canvas.height = visibleLayers[0].current?.height ?? 0;

  const context = getCleanCanvasContext(canvas);
  visibleLayers.forEach((canvasRef) => {
    if (canvasRef.current) {
      context.drawImage(canvasRef.current, 0, 0);
    }
  });

  return canvas;
};
